import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Lock,
  LogIn,
  Mail,
  User,
  UserPlus,
} from "lucide-react";
import siteLogo, { SITE_LOGO_ALT } from "@/assets/brandLogo";
import type { SignupChosenRole } from "@/lib/authApi";
import {
  buildFullName,
  decodeUserFromToken,
  getContributorAccessToken,
  resendSignupOtp,
  sendSignupOtpEmail,
  setContributorTokens,
  signupComplete,
  updateProfileRemote,
  validateSignupPassword,
  verifySignupOtp,
} from "@/lib/authApi";
import { setStoredPortalRbac } from "@jl/lib/jainLexiconRbac";

type RegisterStep = "details" | "otp";

const RESEND_SECONDS = 60;

const CONTRIBUTOR_ROLE: SignupChosenRole = "contributor";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signInContributor, hydrateContributorFromStoredTokens } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpForEmail, setOtpForEmail] = useState("");
  const [signupCodeReference, setSignupCodeReference] = useState("");
  const [registerStep, setRegisterStep] = useState<RegisterStep>("details");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [resendIn, setResendIn] = useState(0);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);
  const [signupFirstName, setSignupFirstName] = useState("");
  const [signupLastName, setSignupLastName] = useState("");

  useEffect(() => {
    setRegisterStep("details");
    setOtpCode("");
    setOtpForEmail("");
    setSignupCodeReference("");
    setShowLoginPassword(false);
    setShowRegPassword(false);
    setShowRegConfirmPassword(false);
    setSignupFirstName("");
    setSignupLastName("");
  }, [activeTab]);

  useEffect(() => {
    if (registerStep !== "otp" || resendIn <= 0) {
      return;
    }
    const t = setInterval(() => {
      setResendIn(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [registerStep, resendIn]);

  useEffect(() => {
    if (searchParams.get("view") === "register") {
      setActiveTab("register");
    }
  }, [searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const { error, redirectTo } = await signInContributor(email, password);
    if (error) {
      toastApiError(error.message);
      setIsLoading(false);
      return;
    }
    toast.success("Logged in successfully!");
    const dest = redirectTo ?? "/";
    setIsLoading(false);
    setTimeout(() => navigate(dest), 500);
  };

  const handleRegisterContinue = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = email.trim();
    if (password !== confirmPassword) {
      toastApiError("Passwords do not match.");
      return;
    }
    const pwErr = validateSignupPassword(password);
    if (pwErr) {
      toastApiError(pwErr);
      return;
    }
    if (!signupFirstName.trim() || !signupLastName.trim()) {
      toastApiError("First and last name are required.");
      return;
    }

    setIsLoading(true);
    const { codeReference, error } = await sendSignupOtpEmail(trimmed, {
      role: CONTRIBUTOR_ROLE,
    });
    setIsLoading(false);
    if (error || !codeReference) {
      toastApiError(error?.message ?? "Missing code reference from server.");
      return;
    }

    setOtpForEmail(trimmed);
    setSignupCodeReference(codeReference);
    setRegisterStep("otp");
    setOtpCode("");
    setResendIn(RESEND_SECONDS);
    toast.success("OTP sent to your email.");
  };

  const handleVerifyAndCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    const currentEmail = otpForEmail || email.trim();
    if (
      otpForEmail &&
      email.trim().toLowerCase() !== otpForEmail.toLowerCase()
    ) {
      toastApiError("Email changed. Go back and request a new OTP.");
      return;
    }

    if (password !== confirmPassword) {
      toastApiError("Passwords do not match.");
      return;
    }
    const pwErr = validateSignupPassword(password);
    if (pwErr) {
      toastApiError(pwErr);
      return;
    }
    if (!signupFirstName.trim() || !signupLastName.trim()) {
      toastApiError("First and last name are required.");
      return;
    }

    if (!signupCodeReference.trim()) {
      toastApiError("Missing OTP session. Go back and request a new code.");
      return;
    }

    setIsLoading(true);
    try {
      const verify = await verifySignupOtp(
        currentEmail,
        otpCode,
        signupCodeReference
      );
      if (verify.error || !verify.verificationToken) {
        toastApiError(verify.error?.message ?? "Missing verification token.");
        return;
      }

      const create = await signupComplete({
        email: currentEmail,
        password,
        password_confirm: confirmPassword,
        verification_token: verify.verificationToken,
        role: CONTRIBUTOR_ROLE,
        account_type: CONTRIBUTOR_ROLE,
      });
      if (create.error) {
        toastApiError(create.error.message);
        return;
      }

      toast.success("Account created. Signing you in…");

      let signedInError: Error | null = null;
      if (create.access) {
        setContributorTokens(create.access, create.refresh ?? null);
        if (create.rbac) {
          setStoredPortalRbac(create.rbac);
        }
        const h = await hydrateContributorFromStoredTokens();
        signedInError = h.error;
      } else {
        const signInResult = await signInContributor(
          currentEmail,
          password
        );
        signedInError = signInResult.error;
      }

      if (signedInError) {
        toastApiError(signedInError.message);
        setRegisterStep("details");
        setOtpCode("");
        setSignupCodeReference("");
        setPassword("");
        setConfirmPassword("");
        setSignupFirstName("");
        setSignupLastName("");
        setActiveTab("login");
        return;
      }

      const tok = getContributorAccessToken();
      const me = tok ? decodeUserFromToken(tok) : null;
      const fn = signupFirstName.trim();
      const ln = signupLastName.trim();
      if (me?.id && fn && ln) {
        const { error: profErr } = await updateProfileRemote(
          me.id,
          {
            email: currentEmail.trim(),
            first_name: fn,
            last_name: ln,
            full_name: buildFullName(fn, ln),
          },
          "portal"
        );
        if (profErr) {
          toastApiError(
            profErr.message ??
              "Signed in, but profile could not be saved. Update it under My account."
          );
        }
      }

      const dest = "/";
      toast.success("Welcome!");

      setRegisterStep("details");
      setOtpCode("");
      setPassword("");
      setConfirmPassword("");
      setSignupCodeReference("");
      setSignupFirstName("");
      setSignupLastName("");
      setActiveTab("login");

      setTimeout(() => navigate(dest), 300);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    const target = otpForEmail || email.trim();
    if (!target || !signupCodeReference.trim()) {
      toastApiError("Email and OTP session required.");
      return;
    }
    setIsLoading(true);
    const { codeReference: nextRef, error } = await resendSignupOtp(
      target,
      signupCodeReference
    );
    setIsLoading(false);
    if (error) {
      toastApiError(error.message);
      return;
    }
    if (nextRef) {
      setSignupCodeReference(nextRef);
    }
    setResendIn(RESEND_SECONDS);
    toast.success("OTP resent.");
  };

  const goBackToDetails = () => {
    setRegisterStep("details");
    setOtpCode("");
    setSignupCodeReference("");
  };

  return (
    <div className="min-h-screen">
      {/* Hero Header */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(220,40%,13%)] via-[hsl(220,38%,18%)] to-[hsl(218,35%,22%)]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="bg-primary/20 float absolute -right-20 -top-20 h-60 w-60 rounded-full blur-3xl" />
          <div className="bg-gold/15 float-delayed absolute -bottom-20 -left-20 h-80 w-80 rounded-full blur-3xl" />
        </div>
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Ccircle cx='10' cy='10' r='1' fill='%23fff'/%3E%3Ccircle cx='30' cy='15' r='1' fill='%23fff'/%3E%3Ccircle cx='50' cy='8' r='1' fill='%23fff'/%3E%3Ccircle cx='70' cy='20' r='1' fill='%23fff'/%3E%3Ccircle cx='90' cy='12' r='1' fill='%23fff'/%3E%3C/svg%3E")`,
            backgroundSize: "150px 75px",
          }}
        />
        <div className="container relative py-5 md:py-12">
          <div className="flex flex-col items-center text-center">
            <div className="animate-fade-in-up relative mb-6">
              <div className="bg-primary/20 absolute inset-0 scale-150 rounded-full blur-xl" />
              <img
                src={siteLogo}
                alt={SITE_LOGO_ALT}
                className="relative h-16 w-auto max-w-[min(320px,90vw)] object-contain md:h-20"
              />
            </div>
            <div
              className="animate-fade-in-up md:mb-6 mb-2 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm"
              style={{ animationDelay: "0.1s" }}
            >
              <LogIn className="text-primary h-8 w-8" />
            </div>
            <h1
              className="animate-fade-in-up md:mb-3 mb-1 font-serif text-2xl font-bold text-white md:text-4xl"
              style={{ animationDelay: "0.2s" }}
            >
              Welcome to Jain Lexicon
            </h1>
            <p
              className="animate-fade-in-up text-white/70"
              style={{ animationDelay: "0.3s" }}
            >
              Sign in or create an account to contribute
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full"
          >
            <path
              d="M0 40V20C360 35 720 5 1080 20C1260 27.5 1350 32.5 1440 35V40H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Auth Form */}
      <section className="from-secondary to-background relative overflow-hidden bg-gradient-to-b py-5 md:py-16">
        <div className="bg-primary/5 absolute right-0 top-0 h-96 w-96 -translate-y-1/2 translate-x-1/2 rounded-full blur-3xl" />
        <div className="container relative">
          <div className="mx-auto max-w-md">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-muted-foreground hover:text-foreground mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>

            <Card className="border-border/50 shadow-xl">
              <CardHeader className="pb-2 text-center">
                <div className="bg-primary/10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                  <Lock className="text-primary h-6 w-6" />
                </div>
                <CardTitle className="font-serif text-2xl">
                  {activeTab === "login"
                    ? "Welcome Back"
                    : registerStep === "otp"
                      ? "Verify your email"
                      : "Create Account"}
                </CardTitle>
                <CardDescription className="text-base">
                  {activeTab === "login"
                    ? "Enter your credentials to continue"
                    : registerStep === "otp"
                      ? `Enter the OTP sent to ${otpForEmail || email}`
                      : "Register with your email—we will send an OTP"}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="mb-6 grid w-full grid-cols-2">
                    <TabsTrigger value="login">Login</TabsTrigger>
                    <TabsTrigger value="register">Register</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    <form onSubmit={handleLogin} className="md:space-y-5 space-y-3">
                      <div className="space-y-2">
                        <Label
                          htmlFor="login-email"
                          className="text-foreground font-medium"
                        >
                          Email
                        </Label>
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          required
                          className="bg-background border-border h-12 text-base"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="login-password"
                          className="text-foreground font-medium"
                        >
                          Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="login-password"
                            type={showLoginPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                            className="bg-background border-border h-12 pr-11 text-base"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowLoginPassword(v => !v)
                            }
                            className="text-muted-foreground hover:text-foreground absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md transition-colors"
                            aria-label={
                              showLoginPassword
                                ? "Hide password"
                                : "Show password"
                            }
                          >
                            {showLoginPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Link
                          to="/forgot-password"
                          className="text-primary text-sm font-medium hover:underline"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground btn-shimmer h-12 w-full text-base"
                      >
                        {isLoading ? (
                          <span className="flex items-center gap-2">
                            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            Signing in...
                          </span>
                        ) : (
                          <>
                            <LogIn className="mr-2 h-5 w-5" />
                            Sign In
                          </>
                        )}
                      </Button>
                      <p className="text-muted-foreground text-center text-xs">
                        Admin or scholar?{" "}
                        <Link
                          to="/jlx-vault-entry"
                          className="text-primary font-medium underline"
                        >
                          Staff login
                        </Link>
                      </p>
                    </form>
                  </TabsContent>

                  <TabsContent value="register">
                    {registerStep === "details" ? (
                      <form
                        onSubmit={handleRegisterContinue}
                        className="md:space-y-5 space-y-3"
                      >
                        <div className="space-y-2">
                          <Label
                            htmlFor="reg-email"
                            className="text-foreground font-medium"
                          >
                            Email
                          </Label>
                          <Input
                            id="reg-email"
                            type="email"
                            placeholder="your@email.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                            className="bg-background border-border h-12 text-base"
                          />
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label
                              htmlFor="reg-first"
                              className="text-foreground font-medium"
                            >
                              First name
                            </Label>
                            <Input
                              id="reg-first"
                              type="text"
                              placeholder="Given name"
                              value={signupFirstName}
                              onChange={e =>
                                setSignupFirstName(e.target.value)
                              }
                              required
                              autoComplete="given-name"
                              className="bg-background border-border h-12 text-base"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label
                              htmlFor="reg-last"
                              className="text-foreground font-medium"
                            >
                              Last name
                            </Label>
                            <Input
                              id="reg-last"
                              type="text"
                              placeholder="Family name"
                              value={signupLastName}
                              onChange={e =>
                                setSignupLastName(e.target.value)
                              }
                              required
                              autoComplete="family-name"
                              className="bg-background border-border h-12 text-base"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor="reg-password"
                            className="text-foreground font-medium"
                          >
                            Password
                          </Label>
                          <div className="relative">
                            <Input
                              id="reg-password"
                              type={
                                showRegPassword ? "text" : "password"
                              }
                              placeholder="At least 8 characters, not digits-only"
                              value={password}
                              onChange={e => setPassword(e.target.value)}
                              required
                              autoComplete="new-password"
                              className="bg-background border-border h-12 pr-11 text-base"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowRegPassword(v => !v)
                              }
                              className="text-muted-foreground hover:text-foreground absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md transition-colors"
                              aria-label={
                                showRegPassword
                                  ? "Hide password"
                                  : "Show password"
                              }
                            >
                              {showRegPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <p className="text-muted-foreground text-xs">
                            Minimum 8 characters and not only digits.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor="reg-confirm"
                            className="text-foreground font-medium"
                          >
                            Confirm password
                          </Label>
                          <div className="relative">
                            <Input
                              id="reg-confirm"
                              type={
                                showRegConfirmPassword
                                  ? "text"
                                  : "password"
                              }
                              placeholder="••••••••"
                              value={confirmPassword}
                              onChange={e =>
                                setConfirmPassword(e.target.value)
                              }
                              required
                              autoComplete="new-password"
                              className="bg-background border-border h-12 pr-11 text-base"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowRegConfirmPassword(v => !v)
                              }
                              className="text-muted-foreground hover:text-foreground absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md transition-colors"
                              aria-label={
                                showRegConfirmPassword
                                  ? "Hide confirm password"
                                  : "Show confirm password"
                              }
                            >
                              {showRegConfirmPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <Button
                          type="submit"
                          disabled={isLoading}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground btn-shimmer h-12 w-full text-base"
                        >
                          {isLoading ? (
                            <span className="flex items-center gap-2">
                              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                              Sending OTP...
                            </span>
                          ) : (
                            <>
                              <Mail className="mr-2 h-5 w-5" />
                              Continue
                            </>
                          )}
                        </Button>
                      </form>
                    ) : (
                      <form
                        onSubmit={handleVerifyAndCreateAccount}
                        className="space-y-5"
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          className="text-muted-foreground -ml-2 h-auto px-2 py-1"
                          onClick={goBackToDetails}
                        >
                          <ArrowLeft className="mr-2 h-4 w-4" />
                          Change name, email, or password
                        </Button>
                        <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 rounded-lg border bg-muted/40 px-3 py-2 text-xs">
                          <span className="inline-flex items-center gap-1">
                            <User className="text-foreground h-3.5 w-3.5" />
                            {signupFirstName.trim() || "—"}{" "}
                            {signupLastName.trim() || ""}
                          </span>
                        </div>
                        <p className="text-muted-foreground text-center text-sm">
                          Creating your{" "}
                          <span className="text-foreground font-semibold">
                            contributor
                          </span>{" "}
                          account for the user portal.
                        </p>
                        <div className="space-y-2">
                          <Label
                            htmlFor="reg-otp"
                            className="text-foreground font-medium"
                          >
                            One-time code
                          </Label>
                          <Input
                            id="reg-otp"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="Enter OTP"
                            value={otpCode}
                            onChange={e =>
                              setOtpCode(e.target.value.replace(/\s/g, ""))
                            }
                            required
                            className="bg-background border-border h-12 text-base tracking-[0.3em]"
                          />
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-muted-foreground text-xs">
                            Changing your email requires a new OTP—use the button
                            above.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={
                              isLoading ||
                              resendIn > 0 ||
                              !otpForEmail ||
                              !signupCodeReference
                            }
                            onClick={() => void handleResendOtp()}
                          >
                            {resendIn > 0
                              ? `Resend OTP (${resendIn}s)`
                              : "Resend OTP"}
                          </Button>
                        </div>

                        <Button
                          type="submit"
                          disabled={isLoading}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground btn-shimmer h-12 w-full text-base"
                        >
                          {isLoading ? (
                            <span className="flex items-center gap-2">
                              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                              Creating account...
                            </span>
                          ) : (
                            <>
                              <UserPlus className="mr-2 h-5 w-5" />
                              Verify OTP & create account
                            </>
                          )}
                        </Button>
                      </form>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
